// Client-side champion/role catalog lookups, from the bundled manifest.
// Agents are referenced by their champion's FIRST NAME (e.g. andre-architect
// -> "André"), with the role as the function subtitle. Falls back to the
// stored display_name / slug when a champion or role isn't in the catalog.

import catalog from '@/catalog/catalog.generated.json';
import { AFFINITY_ORDER } from '@/lib/champion-details.js';

const CHAMPION = new Map(catalog.champions.map((c) => [c.slug, c]));
const ROLE = new Map(catalog.roles.map((r) => [r.slug, r]));

// role slug -> champions with a declared affinity for it, from each
// champion's compatibleRoles. Built once at module load.
const ROLE_CHAMPION_MAP = catalog.champions.reduce((map, c) => {
  for (const { role, affinity } of (c.compatibleRoles ?? [])) {
    (map[role] ??= []).push({ slug: c.slug, affinity });
  }
  return map;
}, {});

/** First name of a champion: "André l'Architect" -> "André". */
export function championFirstName(slug) {
  const dn = CHAMPION.get(slug)?.displayName;
  return dn ? dn.split(/\s+/)[0] : null;
}

/** Full champion display name, e.g. "André l'Architect". */
export function championName(slug) {
  return CHAMPION.get(slug)?.displayName ?? slug ?? '';
}

/** Champion mental model sentence, e.g. "Sees structure, decides shape". */
export function championMentalModel(slug) {
  return CHAMPION.get(slug)?.mentalModel ?? null;
}

/** Champion meta-category slug, e.g. "builders". */
export function championMetaCategory(slug) {
  return CHAMPION.get(slug)?.metaCategory ?? null;
}

/**
 * Primary label for an agent: the champion's first name, falling back to the
 * stored display_name (custom hires, or champions absent from the catalog).
 *
 * @param {{ champion_slug?: string, display_name?: string }} agent
 */
export function agentLabel(agent) {
  return championFirstName(agent?.champion_slug) ?? agent?.display_name ?? '';
}

/** Human-readable role/function label: "architect" -> "Architect". */
export function roleLabel(slug) {
  return ROLE.get(slug)?.displayName ?? slug ?? '';
}

/**
 * The champion with the highest declared affinity for a role, e.g.
 * { slug: 'andre-architect', affinity: 'tres-haute' }. Names are never the
 * role's identity — this is affinity metadata only, for a demoted caption.
 * Returns null when no champion declares an affinity for the role.
 */
export function bestChampionForRole(slug) {
  const matches = ROLE_CHAMPION_MAP[slug];
  if (!matches?.length) return null;
  return [...matches].sort(
    (a, b) => AFFINITY_ORDER.indexOf(a.affinity) - AFFINITY_ORDER.indexOf(b.affinity),
  )[0];
}
